import { Suspense, lazy, useEffect, useState } from "react";
import SectionPage from "@/components/common/section-page";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList } from "@/components/ui/breadcrumb";
import CardTitle from "@/components/common/card-title";
import AwesomeCard from "@/components/common/awesome-card";
import MediaGallery from "@/components/common/media-gallery";
import { getCulinaryExperiences, CulinaryExperience } from "@/lib/data/culinary-experiences";

// Importer le formulaire de manière dynamique
const CulinaryExperienceForm = lazy(
    () => import("@/components/forms/culinary-experience-form")
);

export default function GoutDIvoire() {
    const [mediaItems, setMediaItems] = useState<any[]>([]);

    useEffect(() => {
        getCulinaryExperiences().then((culinaryExperiences) => {
            const items = culinaryExperiences.map((experience: CulinaryExperience) => {
                if (experience.mediaType === 'image') {
                    return {
                        type: "image",
                        src: experience.mediaUrl,
                        alt: experience.title || "Expérience culinaire ivoirienne"
                    };
                } else {
                    const videoSource = (() => {
                        if (experience.mediaUrl.includes('youtube.com') || experience.mediaUrl.includes('youtu.be')) {
                            const youtubeId = experience.mediaUrl.includes('v=')
                                ? experience.mediaUrl.split('v=')[1].split('&')[0]
                                : experience.mediaUrl.split('/').pop();
                            return { type: 'youtube', id: youtubeId } as const;
                        } else if (experience.mediaUrl.includes('vimeo.com')) {
                            const vimeoId = experience.mediaUrl.split('/').pop();
                            return { type: 'vimeo', id: vimeoId } as const;
                        } else {
                            return { type: 'local', src: experience.mediaUrl } as const;
                        }
                    })();

                    return {
                        type: "video",
                        alt: experience.title || "Vidéo culinaire ivoirienne",
                        videoSource,
                        thumbnail: experience.thumbnailUrl
                    };
                }
            });
            setMediaItems(items);
        });
    }, []);

    return (
        <div className="flex flex-col gap-6 pb-6 bg-slate-100">
            <CardTitle
                title="Goût d'Ivoire"
                backgroundImgSrc="/images/card-title/gout-divoire.jpg"
            />

            {/* Breadcrumb */}
            <SectionPage sectionType="2xl">
                <div className="flex justify-between items-center">
                    <Breadcrumb>
                        <BreadcrumbList>
                            <BreadcrumbItem>
                                <BreadcrumbLink href="/">Accueil</BreadcrumbLink>
                            </BreadcrumbItem>
                            <BreadcrumbItem>
                                <BreadcrumbLink href="/ivoirexperience">Ivoire Experience</BreadcrumbLink>
                            </BreadcrumbItem>
                            <BreadcrumbItem>
                                <BreadcrumbLink href="/ivoirexperience/gout-divoire">Goût d'Ivoire</BreadcrumbLink>
                            </BreadcrumbItem>
                        </BreadcrumbList>
                    </Breadcrumb>
                </div>
            </SectionPage>

            {/* Introduction */}
            <SectionPage sectionType="2xl">
                <AwesomeCard className="bg-white text-muted-foreground" titleClassName="text-bleu-marine">
                    <div className="prose max-w-none">
                        <h2 className="text-2xl font-exo-bold text-bleu-marine mb-4">Découvrez la richesse culinaire ivoirienne</h2>
                        <p>
                            La Côte d'Ivoire, terre de saveurs et de traditions culinaires, offre une palette gustative
                            exceptionnelle qui reflète la diversité de ses peuples et de ses régions. Du célèbre attiéké
                            au garba en passant par le foutou et la sauce graine, chaque plat raconte une histoire et
                            invite au voyage sensoriel.
                        </p>
                        <p>
                            Explorez notre galerie d'expériences culinaires et laissez-vous tenter par les saveurs
                            authentiques qui font la renommée de la gastronomie ivoirienne.
                        </p>
                    </div>
                </AwesomeCard>
            </SectionPage>

            {/* Galerie d'expériences culinaires */}
            <SectionPage sectionType="2xl">
                <AwesomeCard
                    title="Galerie des saveurs ivoiriennes"
                    className="bg-white text-muted-foreground"
                    titleClassName="text-bleu-marine"
                >
                    <Suspense fallback={<div className="h-96 flex items-center justify-center">Chargement de la galerie...</div>}>
                        <MediaGallery
                            media={mediaItems}
                            aspectRatio="video"
                            gridClassName="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6"
                        />
                    </Suspense>
                </AwesomeCard>
            </SectionPage>

            {/* Formulaire de partage d'expérience */}
            <SectionPage sectionType="2xl">
                <AwesomeCard
                    title="Partagez votre expérience culinaire"
                    className="bg-white text-muted-foreground"
                    titleClassName="text-bleu-marine"
                >
                    <div className="max-w-2xl mx-auto">
                        <p className="mb-6 text-lg">
                            Vous avez découvert un plat ivoirien qui vous a marqué ? Partagez votre expérience culinaire
                            et contribuez à faire connaître la richesse gastronomique de la Côte d'Ivoire !
                        </p>
                        <Suspense fallback={<div className="h-96 flex items-center justify-center">Chargement du formulaire...</div>}>
                            <CulinaryExperienceForm />
                        </Suspense>
                    </div>
                </AwesomeCard>
            </SectionPage>
        </div>
    );
}
